import React, { useCallback, useEffect, useMemo, useState } from 'react'
import animationData from 'assets/lotties/car-loading-animation.json'
import animationDataMotus from 'assets/lotties/car-loading-animation-motus.json'
import { useLottie } from 'lottie-react'
import { useSettings } from 'core/hooks/settings/useSettings'

interface Props {
  /**
   * Custom title or message to display instead of the default loading text.
   */
  overrideTitle?: string | React.ReactElement
  /**
   * Variant to switch between animations
   */
  variant?: 'everlance' | 'motus'
}

/**
 * AppLoadingIndicator component displays a loading animation and message.
 * Supports custom titles and cycles through default loading messages.
 */
const AppLoadingIndicator = ({ overrideTitle, variant }: Props) => {
  const [textIndex, setTextIndex] = useState(0)
  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE }
    }
  } = useSettings()
  const loadingText = [
    'Loading...',
    'Fueling our engines...',
    'A few more seconds...'
  ]

  const defaultOptions = useMemo(
    () => ({
      loop: true,
      autoplay: true,
      animationData:
        variant === 'motus' || ENABLE_DRIVER_EXPERIENCE
          ? animationDataMotus
          : animationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice'
      },
      style: {
        height: 150,
        width: 300,
        margin: '0 auto'
      }
    }),
    [variant, ENABLE_DRIVER_EXPERIENCE]
  )

  const { View } = useLottie(defaultOptions)

  const incrementIndex = useCallback((currentIndex: number) => {
    if (currentIndex === 2) {
      setTextIndex(0)
      return false
    }
    setTextIndex(currentIndex + 1)
  }, [])

  useEffect(() => {
    const textInterval = setInterval(() => incrementIndex(textIndex), 3000)
    return () => clearInterval(textInterval)
  }, [textIndex, incrementIndex])

  return (
    <div className="grid place-content-center h-[80vh]">
      {View}
      <h2 className="text-center">{overrideTitle || loadingText[textIndex]}</h2>
    </div>
  )
}

export default AppLoadingIndicator
